import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';

interface DialogData {
  currentTab?: string;
}

@Component({
  standalone: true,
  selector: 'app-observations-filter-dialog',
  imports: [CommonModule, MatButtonModule, MatFormFieldModule, MatSelectModule, MatInputModule, MatCheckboxModule, ReactiveFormsModule],
  template: `
  <h2 mat-dialog-title>Advanced Observation Filters</h2>
  <div mat-dialog-content [formGroup]="form">
    <div style="display:flex; gap:12px; align-items:center; flex-wrap:wrap; margin-bottom:12px;">
      <div style="flex:1; min-width:240px;">
        <mat-form-field appearance="outline" style="width:100%;">
          <mat-label>Search observation</mat-label>
          <input matInput [formControl]="codeSearch" placeholder="Type to filter by name or code" />
        </mat-form-field>
        <mat-form-field appearance="outline" style="width:100%; margin-top:6px;">
          <mat-label>Observation (name)</mat-label>
          <mat-select formControlName="observationCode">
            <mat-option [value]="''">(Any)</mat-option>
            <mat-option *ngFor="let o of filteredOptions" [value]="o.code">{{o.display}}</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <mat-form-field appearance="outline" style="width:200px;">
        <mat-label>Date field</mat-label>
        <mat-select formControlName="dateField">
          <mat-option value="date">Obtained (date)</mat-option>
          <mat-option value="value-date">Value date</mat-option>
          <mat-option value="_lastUpdated">Last updated</mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <div style="display:flex; gap:12px; align-items:center; margin-bottom:12px;">
      <div style="flex:1">
        <label class="caption">From</label>
        <input matInput type="date" formControlName="fromDate" style="width:100%; padding:8px; box-sizing:border-box;" />
      </div>
      <div style="flex:1">
        <label class="caption">To</label>
        <input matInput type="date" formControlName="toDate" style="width:100%; padding:8px; box-sizing:border-box;" />
      </div>
    </div>

    <div style="margin-bottom:8px">
      <div style="font-weight:600; margin-bottom:6px">Status</div>
      <div style="display:flex; gap:12px; flex-wrap:wrap">
        <mat-checkbox *ngFor="let s of statusOptions" [formControl]="statusControls[s]">{{s}}</mat-checkbox>
      </div>
    </div>
  </div>
  <div mat-dialog-actions style="display:flex; gap:8px; justify-content:flex-end;">
    <button mat-button (click)="close()">Cancel</button>
    <button mat-flat-button color="primary" (click)="apply()">Apply</button>
  </div>
  `
})
export class ObservationsFilterDialogComponent implements OnInit {
  private data = inject(MAT_DIALOG_DATA) as DialogData;
  private dialogRef = inject(MatDialogRef) as MatDialogRef<ObservationsFilterDialogComponent>;
  private selectService = inject(FormFieldsSelectDataService);

  form: FormGroup = new FormGroup({
    observationCode: new FormControl(''),
    dateField: new FormControl('_lastUpdated'),
    fromDate: new FormControl(''),
    toDate: new FormControl('')
  });

  codeSearch: FormControl = new FormControl('');

  statusOptions = ['registered', 'preliminary', 'final', 'amended', 'corrected', 'cancelled', 'entered-in-error', 'unknown'];
  statusControls: Record<string, FormControl> = {} as any;

  allOptions: Array<{ code: string; display: string; group?: string }> = [];
  filteredOptions: Array<{ code: string; display: string; group?: string }> = [];

  ngOnInit(): void {
    for (const s of this.statusOptions) this.statusControls[s] = new FormControl(false);
    // load codes from backend CodeSystem
    this.selectService.getObservationCodeOptionsFromServer().then((opts) => {
      this.allOptions = opts || [];
      this.applyTabFilter();
    }).catch(() => { this.allOptions = []; this.applyTabFilter(); });

    this.codeSearch.valueChanges?.pipe(debounceTime(200), distinctUntilChanged(), map((v) => (v || '').toString().toLowerCase())).subscribe((term) => {
      if (!term) {
        this.applyTabFilter();
        return;
      }
      this.filteredOptions = (this.allOptions || []).filter(o => (o.display || '').toLowerCase().includes(term) || (o.code || '').toLowerCase().includes(term));
    });
  }

  private applyTabFilter() {
    const tab = (this.data?.currentTab || '').toString().toLowerCase();
    if (!tab || tab === 'others') {
      this.filteredOptions = this.allOptions.slice();
      return;
    }
    const map: Record<string, string[]> = {
      'vital-signs': ['vitals'],
      'exam': ['examAndSymptoms','examandsymptoms','examandsymptom'],
      'physical-exam': ['examAndSymptoms','examandsymptoms'],
      'patient-symptoms': ['examAndSymptoms']
    };
    const groups = map[tab] ?? [tab];
    this.filteredOptions = this.allOptions.filter(o => !o.group || groups.includes(o.group));
  }

  close() { this.dialogRef.close(); }

  apply() {
    const values: any = {};
    const code = this.form.get('observationCode')?.value;
    if (code) values['code'] = code;

    const from = this.form.get('fromDate')?.value;
    const to = this.form.get('toDate')?.value;
    const dateField = this.form.get('dateField')?.value || 'date';
    const dateVals: string[] = [];
    if (from) dateVals.push(`ge${from}`);
    if (to) dateVals.push(`le${to}`);
    if (dateVals.length) values[dateField] = dateVals;

    const selectedStatuses = this.statusOptions.filter(s => this.statusControls[s].value);
    if (selectedStatuses.length) values['status'] = selectedStatuses;

    this.dialogRef.close(values);
  }
}
